import React, {useState, useEffect, useRef} from 'react';
import {
  View,
  Text,
  Image,
  ImageBackground,
  Pressable,
  Modal,
  ActivityIndicator,
  TextInput,
  ScrollView,
  Animated,
} from 'react-native';
import Icon, {IconValue} from '../Shared/Icon';
import {
  DynamicAttachment,
  DynamicAttachmentsProps,
} from '../../Types/ControlTypes';
import {ControlsStyleSheet} from '../../Styles/Shared/Controls';
import Colors from '../../Styles/Shared/Colors';
import {Images} from '../../Constants/Images';
import AttachmentService from '../../Services/AttachmentService';
import {IAttachmentService} from '../../Services/Interfaces/IAttachmentService';
import {usePowerSyncData} from '../../Providers/PowerSyncDataProvider';
import axios, {AxiosError} from 'axios';
import DismissModal from '../Shared/DismissModal';
import {CommonStyleSheet} from '../../Styles/Shared/CommonStyles';
import {Document} from '../../Models/PowerSyncModels/models';
import DynamicSelect from './DynamicSelect';
import {ReportsStyleSheet} from '../../Styles/ReportsStyles';
import {ILogService} from '../../Services/Interfaces/ILogService';
import LogService from '../../Services/LogService';
import {UUID} from 'bson';
import DynamicTextArea from './DynamicTextArea';
import CustomPressable from '../Shared/CustomPressable';

export type UriSasPair = {
  uri: string;
  sasUri: string;
};

export type UploadingAttachment = {
  fileName: string;
  currentChunk: number;
  maxChunk: number;
  fileId: string | undefined;
};

const DynamicAttachments = (
  props: DynamicAttachmentsProps,
): React.ReactElement => {
  const [mode] = useState(props.config?.mode ?? 'attachments');
  const [maxCount, setMaxCount] = useState(0);
  const [maxSize, setMaxSize] = useState(0);
  const [attachments, setAttachments] = useState<DynamicAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState<
    UploadingAttachment | undefined
  >();
  const [error, setError] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState<DynamicAttachment | null>(null);
  const [previewPhotoDesc, setPreviewPhotoDesc] = useState('');
  const [editPreviewPhotoDesc, setEditPreviewPhotoDesc] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [sasUris, setSasUris] = useState<UriSasPair[]>([]);

  const [types, setTypes] = useState<{label: string; value: string}[]>([]);
  const [docs, setDocuments] = useState<Document[]>([]);
  const [isShowingDocumentLibrary, setIsShowingDocumentLibrary] =
    useState<boolean>(false);

  const [documentSearch, setDocumentSearch] = useState('');
  const [documentType, setDocumentType] = useState('');
  const [selectedDocuments, setSelectedDocuments] = useState<
    DynamicAttachment[]
  >([]);
  const [isShowingDocumentFilters, setIsShowingDocumentFilters] =
    useState<boolean>(false);

  const [isShowingQuestionModal, setIsShowingQuestionModal] = useState(false);

  const attachmentService: IAttachmentService = new AttachmentService(() => {});

  const {documentTypes, documents} = usePowerSyncData();

  const isMounted = useRef(false);

  const progressMap = useRef<Record<string, Animated.Value>>({}).current;

  const inputRef = React.createRef<HTMLInputElement>();

  const imageTypes = ['image/png', 'image/jpg', 'image/jpeg'];

  const logService: ILogService = new LogService();

  useEffect(() => {
    fetchDocumentLibrary();

    if (props.config?.maxAttachments) setMaxCount(props.config?.maxAttachments);
    else if (process.env.REACT_APP_MAX_ATTACHMENTS_COUNT)
      setMaxCount(parseInt(process.env.REACT_APP_MAX_ATTACHMENTS_COUNT));

    if (props.config?.maxFileSizeMB) setMaxSize(props.config?.maxFileSizeMB);
    else if (process.env.REACT_APP_MAX_ATTACHMENTS_SIZE_MB)
      setMaxSize(parseInt(process.env.REACT_APP_MAX_ATTACHMENTS_SIZE_MB));

    if (props.value) {
      let parsedAttachments = JSON.parse(props.value!) as DynamicAttachment[];
      setAttachments(parsedAttachments);

      const getSasUri = async () => {
        let newSasUris: UriSasPair[] = [];
        for (let i = 0; i < parsedAttachments.length; i++) {
          try {
            let fullUri = await attachmentService.getSasUri(
              parsedAttachments[i].blobUri!,
            );

            newSasUris.push({
              uri: parsedAttachments[i].blobUri!,
              sasUri: fullUri,
            });

            if (parsedAttachments[i].blobThumbnailUri) {
              let thumbnailUri = await attachmentService.getSasUri(
                parsedAttachments[i].blobThumbnailUri!,
              );

              newSasUris.push({
                uri: parsedAttachments[i].blobThumbnailUri!,
                sasUri: thumbnailUri,
              });
            }
          } catch (err) {
            await logService.sendLog(JSON.stringify(error));
          }
        }

        setSasUris(newSasUris);
      };

      getSasUri();
    }

    isMounted.current = true;
  }, []);

  useEffect(() => {
    let validValue = valid();
    let error = '';
    let newValue = JSON.stringify(attachments);
    props.onChange!(props.controlId, props.controlTypeId, newValue, validValue);

    if (!validValue) {
      error =
        mode === 'photos'
          ? 'You need to add photos'
          : 'You need to add attachments';
    }

    if (props.showError) setError(error);
  }, [props.showError, attachments]);

  useEffect(() => {
    if (inputRef) {
      if (props.setShowFilePicker)
        props.setShowFilePicker(() => {
          showFilePicker();
        });

      if (props.setOpenDocumentLibrary)
        props.setOpenDocumentLibrary(() => {
          openDocumentLibrary();
        });
    }
  }, [inputRef]);

  const fetchDocumentLibrary = async () => {
    let typesArray = documentTypes
      .filter(x => !x.isDeleted && x.isActive)
      .map(x => ({label: x.name, value: x._id}))
      .sort((a, b) => (a.label > b.label ? 1 : a.label < b.label ? -1 : 0));

    setTypes(typesArray);

    let docs = documents
      .filter(
        x =>
          x.documentTypeId &&
          typesArray.some(t => t.value === String(x.documentTypeId)),
      )
      .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));

    setDocuments(docs);
  };

  function getFileName(attachment: DynamicAttachment): string {
    let fileName = attachment.name ?? '';
    if (fileName) {
      let extPosition = fileName.lastIndexOf('.');
      if (extPosition != -1) {
        let namePart = fileName.substring(0, extPosition);
        if (namePart.length > 24) namePart = namePart.substring(0, 26) + '..';

        fileName =
          namePart + '.' + fileName.substring(extPosition + 1, fileName.length);
      }
    }

    return fileName;
  }

  function viewPhoto(index: number): void {
    let photo = attachments[index];

    if (!photo || !photo.blobUri) return;

    if (photo) {
      setPreviewPhoto(photo);
      setPreviewPhotoDesc(photo.description ?? '');
      setEditPreviewPhotoDesc(false);
      setShowModal(true);
    }
  }

  /*TODO will need to cache these attachments sas urls so client doesnt have to keep generating new sas urls. Need to keep track of sas expiries too
  
  const getBlobExpirationTime = (blobSasUrl: string) => {
    const blobFullUrl = new URLSearchParams(blobSasUrl);
    return blobFullUrl.get("se") || ""; //se = signature expiry
  };

  const updateBlobSasToken = async (blobUrl: string) => {
    props.setIsLoading(true);
    let sasUrl = await attachmentService.getSasUri(blobUrl);

    let newSasUris = [...sasUris];

    let sasUriToUpdate = newSasUris.find((x) => x.uri === blobUrl);
    sasUriToUpdate!.sasUri = sasUrl;

    setSasUris(newSasUris);

    props.setIsLoading(false);
    return sasUrl;
  };
*/

  async function showFilePicker(): Promise<void> {
    if (attachments.length >= maxCount) {
      setError('You have reached the maximum number of attachments');
      return;
    } else setError('');

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.click();
    }
  }

  async function selectFiles(files: FileList | null): Promise<void> {
    if (files) {
      try {
        // TODO: Implement attachments service
        // let uris = await attachmentService.addAttachments(
        //   props.submissionId,
        //   props.controlId,
        //   props.config?.allowedTypes!,
        // );
        props.setIsLoading(true);

        let maxByteSize = maxSize * 1024 * 1024;
        for (let i = 0; i < files.length; i++) {
          if (files[i].size >= maxByteSize) {
            props.setIsLoading(false);
            setError('File size must be less than ' + maxSize + 'MB');
            return;
          }
        }

        let newAttachments = [...attachments];
        let newSasUris = [...sasUris];
        for (let i = 0; i < files.length; i++) {
          let fileType = files[i].type;
          let fileName = files[i].name.replaceAll(' ', '_');
          let fileId = new UUID().toString();
          let maxChunk = 0;

          let extension = fileName.split('.').pop()?.toLowerCase();
          if (extension === 'heic' || extension === 'heif')
            fileType = 'image/' + extension;

          // Check against the latest count atomically
          let currentCount = 0;
          setAttachments(prev => {
            currentCount = prev.length;
            return prev;
          });

          if (currentCount >= maxCount) {
            props.setIsLoading(false);
            setError('You have reached the maximum number of attachments');
            break;
          }

          let uris: string[] = [];

          try {
            let fullImageUri = await attachmentService.pushAttachmentChunks(
              fileId,
              props.attachmentsType,
              fileName,
              files[i],
              maxChunks => {
                maxChunk = maxChunks;
                setUploadingAttachment({
                  fileName: fileName,
                  maxChunk: maxChunks,
                  currentChunk: 0,
                  fileId: fileId,
                });
              },
              currentChunk => {
                setUploadingAttachment({
                  fileName: fileName,
                  maxChunk: maxChunk,
                  currentChunk: currentChunk,
                  fileId: fileId,
                });
              },
            );
            setUploadingAttachment(undefined);
            uris.push(fullImageUri);

            if (fileType.includes('image/')) {
              let thumbnailFile = await attachmentService.createThumbnail(
                files[i],
              );

              let thumbnailUri = await attachmentService.pushAttachmentChunks(
                fileId,
                props.attachmentsType,
                thumbnailFile.name,
                thumbnailFile,
              );

              uris.push(thumbnailUri);
            }
          } catch (err: any | AxiosError) {
            props.setIsLoading(false);

            if (axios.isAxiosError(err)) {
              if (err.response?.status === 413) {
                setError('File exceeds size limits.');
                return;
              }
            }

            setError('Please try re-uploading your files');

            await logService.sendLog(JSON.stringify(error));
            return;
          }

          if (
            (fileType === 'image/heic' || fileType === 'image/heif') &&
            uris[0].match(/.+\.png$/)
          ) {
            //Rename type/extension if heic/heif file was converted to png
            fileType = 'image/png';
            fileName = fileName.split('.')[0] + '.png';
          }

          let newAttachmentItem: DynamicAttachment;

          if (fileType.includes('image/')) {
            //uri[0] is thumbmail, uri[1] is full file
            //Something wrong upload failed
            if (uris.length != 2) {
              props.setIsLoading(false);
              setError('Please try re-uploading your files');
              return;
            }

            newAttachmentItem = {
              refId: props.submissionId,
              refType: props.attachmentsType,
              controlId: props.controlId,
              fileId: fileId,
              uri: '',
              name: fileName,
              type: fileType,
              size: files[i].size,
              blobUri: uris[0],
              blobThumbnailUri: uris[1],
            };

            let blobUri = await attachmentService.getSasUri(uris[0]!);
            let thumbnailUri = await attachmentService.getSasUri(uris[1]!);

            setSasUris(prevSas => [
              ...prevSas,
              {uri: uris[0], sasUri: blobUri},
              {uri: uris[1], sasUri: thumbnailUri},
            ]);
          } else {
            newAttachmentItem = {
              refId: props.submissionId,
              refType: props.attachmentsType,
              controlId: props.controlId,
              fileId: fileId,
              uri: '',
              name: fileName,
              type: fileType,
              size: files[i].size,
              blobThumbnailUri: null,
              blobUri: uris[0],
            };
          }

          // Atomic updater simply updates state; the useEffect catches it and safely reports it up
          setAttachments(prevAttachments => [
            ...prevAttachments,
            newAttachmentItem,
          ]);
        }

        props.setIsLoading(false);
      } catch (error) {
        await logService.sendLog(JSON.stringify(error));
        props.setIsLoading(false);
        setUploadingAttachment(undefined);
      }
    }
  }

  async function removeAttachment(index: number): Promise<void> {
    let deletedAttachment: DynamicAttachment | undefined;

    setAttachments(prev => {
      const copy = [...prev];
      const removed = copy.splice(index, 1);
      if (removed.length > 0) deletedAttachment = removed[0];
      return copy;
    });

    if (deletedAttachment) {
      if (deletedAttachment.blobUri)
        await attachmentService.deleteBlobAttachment(deletedAttachment.blobUri);

      if (deletedAttachment.blobThumbnailUri)
        await attachmentService.deleteBlobAttachment(
          deletedAttachment.blobThumbnailUri,
        );
    }
  }

  // TODO: Implement attachments service
  async function downloadAttachment(index: number): Promise<void> {
    // let photo = attachments[index];
    // if (photo) {
    //   // Copy file to Downloads folder
    //   let path = RNFS.DownloadDirectoryPath + '/' + photo.name;
    //   await copyFile(photo.uri, path);
    //   //Open file
    //   await FileViewer.open(path, {showOpenWithDialog: true});
    // }
    let attachment = attachments[index];

    if (attachment && attachment.blobUri && attachment.name)
      await attachmentService.downloadAttachment(
        attachment.blobUri,
        attachment.name,
      );
  }

  function openDocumentLibrary(): void {
    let _selectedDocuments = attachments.filter(
      x => x.refType == 'company-document',
    );

    setSelectedDocuments(_selectedDocuments);
    setIsShowingDocumentLibrary(true);
  }

  function selectDocument(documentAttachment: DynamicAttachment): void {
    let newSelection = [...selectedDocuments];

    let index = newSelection.findIndex(
      x => x.refId == documentAttachment.refId,
    );
    if (index === -1) newSelection.push(documentAttachment);
    else newSelection.splice(index, 1);

    setSelectedDocuments(newSelection);
  }

  async function saveDocuments(): Promise<void> {
    setIsShowingDocumentLibrary(false);
    let otherAttachmentsCount = attachments.filter(
      x => x.refType !== 'company-document',
    ).length;
    if (selectedDocuments.length + otherAttachmentsCount > maxCount) {
      setError('You have reached the maximum number of attachments');
      return;
    }

    let toRemove = [];
    setError('');
    props.setIsLoading(true);

    try {
      let newAttachments = [...attachments];
      let newSasUris = [...sasUris];

      let previousSelectedDocs = attachments
        .filter(x => x.refType === 'company-document')
        .map(x => x.refId);
      let newSelectedDocs = selectedDocuments.map(x => x.refId);

      //Remove previous selected documents not in the new selection
      for (let i = 0; i < previousSelectedDocs.length; i++) {
        if (!newSelectedDocs.includes(previousSelectedDocs[i])) {
          let attachmentIndex = newAttachments.findIndex(
            x => x.refId == previousSelectedDocs[i],
          );

          if (attachmentIndex > -1) {
            let blobUri = newAttachments[attachmentIndex].blobUri;
            if (blobUri) toRemove.push(blobUri);
            let blobUriIndex = newSasUris.findIndex(x => x.uri === blobUri);
            if (blobUriIndex > -1) newSasUris.splice(blobUriIndex, 1);

            let blobThumbnailUri =
              newAttachments[attachmentIndex].blobThumbnailUri;
            if (blobThumbnailUri) toRemove.push(blobThumbnailUri);
            let blobThumbnailUriIndex = newSasUris.findIndex(
              x => x.uri === blobThumbnailUri,
            );
            if (blobThumbnailUriIndex > -1)
              newSasUris.splice(blobThumbnailUriIndex, 1);
          }

          newAttachments.splice(attachmentIndex, 1);
        }
      }

      for (let i = 0; i < selectedDocuments.length; i++) {
        let attachment = selectedDocuments[i];
        if (
          attachment &&
          !previousSelectedDocs.includes(attachment.refId) &&
          attachment.blobUri
        ) {
          let copyAttachment: DynamicAttachment = {...attachment};
          newAttachments.push(copyAttachment);

          copyAttachment.blobUri = await attachmentService.copyBlobAttachment(
            copyAttachment.blobUri!,
          );
          let blobUri = await attachmentService.getSasUri(
            copyAttachment.blobUri!,
          );
          newSasUris.push({
            uri: copyAttachment.blobUri!,
            sasUri: blobUri,
          });

          if (copyAttachment.blobThumbnailUri) {
            copyAttachment.blobThumbnailUri =
              await attachmentService.copyBlobAttachment(
                copyAttachment.blobThumbnailUri!,
              );

            let thumbnailUri = await attachmentService.getSasUri(
              copyAttachment.blobThumbnailUri!,
            );
            newSasUris.push({
              uri: copyAttachment.blobThumbnailUri!,
              sasUri: thumbnailUri,
            });
          }
        }
      }

      setAttachments(newAttachments);
      setSasUris(newSasUris);
    } catch (error) {
      setError('Could not add document from document library.');
      await logService.sendLog(JSON.stringify(error));
    } finally {
      setIsShowingDocumentLibrary(false);

      props.setIsLoading(false);
    }

    //Delete attachments asyn to no block user interface
    for (let i = 0; i < toRemove.length; i++) {
      try {
        await attachmentService.deleteBlobAttachment(toRemove[i]);
      } catch (error) {
        let logError = {
          type: 'Attachment Error',
          msg: error,
        };

        await logService.sendLog(JSON.stringify(logError));
      }
    }
  }

  async function saveAttachmentDescription(): Promise<void> {
      let newAttachments = [...attachments];

      let attachmentIndex = newAttachments.findIndex(x => x.fileId == previewPhoto?.fileId)
      if(attachmentIndex > -1) {
        newAttachments[attachmentIndex].description = previewPhotoDesc;

        setAttachments(newAttachments);
        setEditPreviewPhotoDesc(false);
      }
  }

  function valid(): boolean {
    let isValid = true;

    if (
      props.config?.required &&
      isMounted.current &&
      attachments.length === 0
    ) {
      isValid = false;
    }

    return isValid;
  }

  const renderPhoto = (
    attachment: DynamicAttachment,
    index: number,
  ): React.ReactElement => {
    let sasUri = sasUris.find(x => x.uri == attachment.blobThumbnailUri);
    let uri = '';

    if (sasUri) uri = sasUri.sasUri;

    let fileName = getFileName(attachment);

    return (
      <CustomPressable
        key={index}
        tooltip={attachment.description ? attachment.description : ''}
        style={[
          ControlsStyleSheet.attachmentsItem,
          {maxWidth: props.itemMaxWidth ?? undefined, overflow: 'visible'},
        ]}>
        <Image 
          source={{uri: uri}}
          style={ControlsStyleSheet.attachmentsItemBG}
          resizeMode="cover"
        />
        {uri ? (
          <View style={ControlsStyleSheet.attachmentsActions}>
            <Pressable
              style={({pressed}) => [
                ControlsStyleSheet.attachmentsAction,
                pressed && {
                  backgroundColor: Colors.lightTeal,
                },
              ]}
              onPress={() => viewPhoto(index)}>
              <Icon icon={'eye'} color={Colors.green} size={20} />
            </Pressable>
            <Pressable
              style={({pressed}) => [
                ControlsStyleSheet.attachmentsAction,
                pressed && {
                  backgroundColor: Colors.lightTeal,
                },
              ]}
              onPress={() => downloadAttachment(index)}>
              <Icon icon={'download'} color={Colors.green} size={20} />
            </Pressable>
            {!props.disabled && (
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.attachmentsAction,
                  pressed && {
                    backgroundColor: Colors.lightTeal,
                  },
                ]}
                onPress={() => removeAttachment(index)}>
                <Icon icon={'trash'} color={Colors.green} size={20} />
              </Pressable>
            )}
          </View>
        ) : (
          <>
            <ActivityIndicator color={Colors.green} size={32} />
            <Text style={ControlsStyleSheet.attachmentsText}>{fileName}</Text>
          </>
        )}
      </CustomPressable>
    );
  };

  const getProgress = (fileId: string) => {
    if (!progressMap[fileId]) {
      progressMap[fileId] = new Animated.Value(0);
    }
    return progressMap[fileId];
  };

  const renderAttachmentUploading = (
    attachment: UploadingAttachment,
  ): React.ReactElement => {
    const progress = getProgress(attachment.fileId || attachment.fileName);
    Animated.timing(progress, {
      toValue: attachment.currentChunk,
      duration: 1000,
      useNativeDriver: false,
    }).start();
    const widthInterpolated = progress.interpolate({
      inputRange: [0, attachment.maxChunk],
      outputRange: ['0%', '100%'],
    });
    return (
      <View
        style={[
          ControlsStyleSheet.uploadingAttachmentItem,
          { maxWidth: props.itemMaxWidth ?? undefined }
        ]}>
        <View style={ControlsStyleSheet.uploadingAttachmentView}>
          <Text style={ControlsStyleSheet.uploadingAttachmentText}>
            {attachment.fileName}
          </Text>
          <View style={ControlsStyleSheet.loadingBarOuter}>
            <Animated.View
              style={[
                ControlsStyleSheet.loadingBarInner,
                {width: widthInterpolated},
              ]}
            />
          </View>
        </View>

        <Text
          style={[
            ControlsStyleSheet.uploadingAttachmentText,
            {fontSize: 20, marginTop: 0},
          ]}>
          Uploading
        </Text>
      </View>
    );
  };

  const renderAttachment = (
    attachment: DynamicAttachment,
    index: number,
  ): React.ReactElement => {
    let fileName = getFileName(attachment);

    return (
      <View
        key={index}
        style={[
          ControlsStyleSheet.attachmentsItem,
          {maxWidth: props.itemMaxWidth ?? undefined},
        ]}>
        {attachment.type === 'application/pdf' ? (
          <Image style={{width: 32, height: 32}} source={Images.PDF} />
        ) : (
          <Icon icon={'file'} color={Colors.green} size={20} />
        )}
        <Text style={ControlsStyleSheet.attachmentsText}>{fileName}</Text>
        <View style={ControlsStyleSheet.attachmentsActions}>
          <Pressable
            style={({pressed}) => [
              ControlsStyleSheet.attachmentsAction,
              pressed && {
                backgroundColor: Colors.lightTeal,
              },
            ]}
            onPress={() => downloadAttachment(index)}>
            <Icon icon={'download'} color={Colors.green} size={20} />
          </Pressable>
          {!props.disabled && (
            <Pressable
              style={({pressed}) => [
                ControlsStyleSheet.attachmentsAction,
                pressed && {
                  backgroundColor: Colors.lightTeal,
                },
              ]}
              onPress={() => removeAttachment(index)}>
              <Icon icon={'trash'} color={Colors.green} size={20} />
            </Pressable>
          )}
        </View>
      </View>
    );
  };

  const renderNotSynced = (
    attachment: DynamicAttachment,
    index: number,
  ): React.ReactElement => {
    let fileName = getFileName(attachment);

    return (
      <View
        key={index}
        style={[
          ControlsStyleSheet.attachmentsItem,
          {maxWidth: props.itemMaxWidth ?? undefined},
        ]}>
        <Icon icon={'syncing'} color={Colors.green} size={20} />
        <Text style={ControlsStyleSheet.attachmentsText}>{fileName}</Text>
        <Text
          style={[
            ControlsStyleSheet.attachmentsText,
            {fontSize: 10, marginTop: 4, textTransform: 'none'},
          ]}>
          This file has not been synced yet.
        </Text>
        <View
          style={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}>
          <Pressable
            style={({pressed}) => [
              ControlsStyleSheet.attachmentsAction,
              {borderColor: Colors.green, borderWidth: 2},
              pressed && {
                backgroundColor: Colors.lightTeal,
              },
            ]}
            onPress={() => setIsShowingQuestionModal(true)}>
            <Icon icon={'question'} color={Colors.green} size={12} />
          </Pressable>
        </View>
        <View style={ControlsStyleSheet.attachmentsActions}>
          {!props.disabled && (
            <Pressable
              style={({pressed}) => [
                ControlsStyleSheet.attachmentsAction,
                pressed && {
                  backgroundColor: Colors.lightTeal,
                },
              ]}
              onPress={() => removeAttachment(index)}>
              <Icon icon={'trash'} color={Colors.green} size={20} />
            </Pressable>
          )}
        </View>
      </View>
    );
  };

  const renderPreviewPhoto = (): React.ReactElement => {
    let sasUri = sasUris.find(x => x.uri == previewPhoto?.blobUri);
    let uri = '';

    if (sasUri) uri = sasUri.sasUri;

    return (
      <Modal
        visible={showModal}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={[ControlsStyleSheet.groupSelectorModal, {paddingHorizontal: 0}]}>
            <View style={[ControlsStyleSheet.groupSelectorModalBar, {marginHorizontal: 20}]}>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={() => {
                  setPreviewPhoto(null);
                  setShowModal(false);
                }}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            <View
              style={{
                flex: 1,
                alignItems: 'center',
                justifyContent: 'center',
                marginVertical: 24,
              }}>
              <Image
                resizeMode='contain'
                style={{width: '100%', height: '100%'}}
                source={{uri: uri}}
              />
            </View>
            <View style={{
              borderBottomColor: Colors.darkestGreen,
              borderBottomWidth: 2,
              marginVertical: 4,
              }} />
            <View style={{
                paddingHorizontal: 12,
                paddingBottom: 12,
              }}>
              <View style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}>
                <Text style={ControlsStyleSheet.label}>Description</Text>
                {!props.disabled && (
                  <View style={{flexDirection: 'row', gap: 6}}>
                    {editPreviewPhotoDesc ? (
                      <Pressable
                        style={({pressed}) => [
                          ControlsStyleSheet.attachmentsAction,
                          {
                            backgroundColor: Colors.darkGreen,
                            marginTop: 6,
                          },
                          pressed && {
                            backgroundColor: Colors.darkGreenTransparent,
                          },
                        ]}
                        onPress={() => saveAttachmentDescription()}>
                        <Icon icon={'save'} color={Colors.white} size={26} />
                      </Pressable>
                    ) : (
                      <Pressable
                        style={({pressed}) => [
                          ControlsStyleSheet.attachmentsAction,
                          {
                            backgroundColor: Colors.darkGreen,
                            marginTop: 6,
                          },
                          pressed && {
                            backgroundColor: Colors.darkGreenTransparent,
                          },
                        ]}
                        onPress={() => setEditPreviewPhotoDesc(true)}>
                        <Icon icon={'pencil'} color={Colors.white} size={20} />
                      </Pressable>
                    )}
                  </View>
                )}
              </View>
              <DynamicTextArea
                label=''
                config={{
                  placeholder: 'Description',
                  required: false,
                  maxLength: 400,
                }}
                value={previewPhotoDesc}
                onChange={(controlId, controlTypeId, value, isValid) =>
                  setPreviewPhotoDesc(value)
                }
                disabled={!editPreviewPhotoDesc}
              />
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderDocumentLibrary = (): React.ReactElement => {
    let filteredDocuments = docs;
    if (documentSearch || documentType) {
      filteredDocuments = filteredDocuments.filter(
        x =>
          x.name.toLowerCase().includes(documentSearch.toLowerCase()) &&
          (!documentType || documentType === String(x.documentTypeId)),
      );
    }

    return (
      <Modal
        visible={isShowingDocumentLibrary}
        transparent={true}
        statusBarTranslucent={true}
        animationType='fade'>
        <View style={ControlsStyleSheet.modalBackground}>
          <View style={ControlsStyleSheet.groupSelectorModal}>
            <View style={ControlsStyleSheet.groupSelectorModalBar}>
              <Text style={ControlsStyleSheet.groupSelectorModalTitle}>
                Document Library
              </Text>
              <Pressable
                style={({pressed}) => [
                  ControlsStyleSheet.groupSelectorModalClose,
                  pressed && {
                    backgroundColor: Colors.darkGreenTransparent,
                    borderRadius: 24,
                  },
                ]}
                onPress={() => setIsShowingDocumentLibrary(false)}>
                <Icon icon={'close'} color={Colors.darkestGreen} size={24} />
              </Pressable>
            </View>
            {renderDocumentLibraryFilters()}
            <ScrollView style={{marginVertical: 20}}>
              {filteredDocuments.map((document, index) => {
                return renderDocument(document, index);
              })}
            </ScrollView>
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.greenButton,
                {alignSelf: 'center', marginTop: 0, marginBottom: 20},
                pressed && {
                  opacity: 0.8,
                },
              ]}
              onPress={saveDocuments}>
              <Text style={CommonStyleSheet.greenButtonText}>Ok</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    );
  };

  const renderDocumentLibraryFilters = (): React.ReactElement => {
    let filtersApplied = false;

    if (documentSearch || documentType) filtersApplied = true;

    return (
      <View>
        <View
          style={[
            CommonStyleSheet.searchContainer,
            {paddingHorizontal: 0, marginTop: 12},
          ]}>
          <View style={CommonStyleSheet.searchBoxContainer}>
            <View style={CommonStyleSheet.searchBoxIcon}>
              <Icon icon={'input-search'} color={Colors.darkGreen} size={24} />
            </View>
            <TextInput
              returnKeyType='search'
              style={CommonStyleSheet.searchBoxInput}
              placeholder='Search'
              placeholderTextColor={Colors.gray}
              value={documentSearch}
              onChangeText={newText => setDocumentSearch(newText)}
            />
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.searchBoxClear,
                {display: documentSearch === '' ? 'none' : 'flex'},
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                  borderRadius: 14,
                },
              ]}
              onPress={() => setDocumentSearch('')}>
              <Icon icon={'input-clear'} color={Colors.darkGreen} size={18} />
            </Pressable>
          </View>
          <View style={[CommonStyleSheet.searchFilters]}>
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.searchFiltersContent,
                pressed && {
                  backgroundColor: Colors.darkGreenTransparent,
                  borderRadius: 40,
                },
                isShowingDocumentFilters && {
                  backgroundColor: Colors.darkGreen,
                  borderRadius: 40,
                },
                filtersApplied && {
                  backgroundColor: Colors.yellow,
                  borderRadius: 40,
                },
              ]}
              onPress={() =>
                setIsShowingDocumentFilters(!isShowingDocumentFilters)
              }>
              <Text
                style={
                  isShowingDocumentFilters || filtersApplied
                    ? CommonStyleSheet.searchFiltersTextLight
                    : CommonStyleSheet.searchFiltersText
                }>
                Filters
              </Text>
              <Icon
                icon={'input-filters'}
                color={
                  isShowingDocumentFilters || filtersApplied
                    ? Colors.white
                    : Colors.green
                }
                size={18}
              />
            </Pressable>
          </View>
        </View>
        {isShowingDocumentFilters && (
          <View
            style={{
              borderWidth: 1,
              borderColor: Colors.green,
              borderRadius: 12,
              padding: 12,
              marginTop: 12,
            }}>
            <Text
              style={{
                color: Colors.darkGreen,
                fontFamily: 'Poppins',
                fontSize: 14,
                fontWeight: '700',
                alignSelf: 'center',
              }}>
              FILTERS
            </Text>
            <DynamicSelect
              label='Document Type'
              config={{options: types}}
              value={documentType}
              onChange={(controlId, controlTypeId, value) => {
                setDocumentType(value);
              }}
            />
            <Pressable
              style={({pressed}) => [
                CommonStyleSheet.smallGreenButton,
                {alignSelf: 'center', marginVertical: 12},
                pressed && {
                  opacity: 0.8,
                },
              ]}
              onPress={() => {
                setDocumentSearch('');
                setDocumentType('');
                setIsShowingDocumentFilters(false);
              }}>
              <Text style={CommonStyleSheet.smallGreenButtonText}>
                Clear Filters
              </Text>
            </Pressable>
          </View>
        )}
      </View>
    );
  };

  const renderDocument = (
    document: Document,
    index: number,
  ): React.ReactElement => {
    let type = types.find(x => x.value === String(document.documentTypeId));

    let icon = 'file';
    let fileType = '';
    let attachment: DynamicAttachment | null = null;
    if (document.attachmentJSON) {
      attachment = JSON.parse(document.attachmentJSON!) as DynamicAttachment;

      fileType = attachment.type ?? '';
      if (attachment?.type?.includes('image')) icon = 'picture';
    }
    if (!attachment) return <></>;

    let checked = attachment?.refId
      ? selectedDocuments.some(x => x.refId == attachment!.refId)
      : false;

    return (
      <Pressable
        key={index}
        style={({pressed}) => [
          ReportsStyleSheet.listItemContainer,
          {padding: 0, marginBottom: 10},
          pressed && {
            backgroundColor: Colors.darkGreenTransparent,
          },
        ]}
        onPress={() => selectDocument(attachment!)}>
        <View style={{marginHorizontal: 12}}>
          <Icon
            icon={icon as IconValue}
            color={Colors.darkestGreen}
            size={24}
          />
        </View>
        <View style={[ReportsStyleSheet.listItemName, {paddingLeft: 12}]}>
          <Text style={[ReportsStyleSheet.listItemDate]} numberOfLines={1}>
            {type?.label}
          </Text>
          <Text style={ReportsStyleSheet.listItemName} numberOfLines={1}>
            {document.name}
          </Text>
          <Text style={ReportsStyleSheet.listItemSubtitle} numberOfLines={1}>
            {fileType}
          </Text>
        </View>
        <View
          style={[
            ControlsStyleSheet.checkBox,
            {marginHorizontal: 12},
            checked && {
              backgroundColor: Colors.teal,
              borderColor: Colors.teal,
            },
          ]}>
          <Icon icon={'checked'} color={Colors.white} size={16} />
        </View>
      </Pressable>
    );
  };

  return (
    <>
      {props.showButtons == false ? (
        <>
          {attachments.map((attachment, index) => {
            if (!attachment.blobUri) return renderNotSynced(attachment, index);
            else if (attachment.type && imageTypes.includes(attachment.type))
              return renderPhoto(attachment, index);
            else return renderAttachment(attachment, index);
          })}
          {uploadingAttachment &&
            renderAttachmentUploading(uploadingAttachment)}
        </>
      ) : (
        <View style={{display: props.visible === false ? 'none' : 'flex'}}>
          <View
            style={[
              ControlsStyleSheet.attachmentsContainer,
              {
                paddingLeft: props.itemMaxWidth
                  ? 0
                  : ControlsStyleSheet.attachmentsContainer.paddingLeft,
              },
            ]}>
            {!props.disabled && (
              <>
                <Pressable
                  style={({pressed}) => [
                    ControlsStyleSheet.attachmentsItem,
                    { flexBasis: 'auto', width: 320, backgroundColor: Colors.green },
                    pressed && {
                      opacity: 0.6,
                    },
                  ]}
                  onPress={showFilePicker}>
                  <Icon icon={'file'} color={Colors.white} size={24} />
                  <Text style={[ControlsStyleSheet.attachmentsText, { color: Colors.white }]}>
                    Add From Device
                  </Text>
                </Pressable>
                {props.config?.showDocumentLibrary === true && (
                  <Pressable
                    style={({pressed}) => [
                      ControlsStyleSheet.attachmentsItem,
                      { flexBasis: 'auto', width: 320, backgroundColor: Colors.green },
                      pressed && {
                        opacity: 0.6,
                      },
                    ]}
                    onPress={openDocumentLibrary}>
                    <Icon icon={'library'} color={Colors.white} size={24} />
                    <Text style={[ControlsStyleSheet.attachmentsText, { color: Colors.white }]}>
                      Add From Document Library
                    </Text>
                  </Pressable>
                )}
              </>
            )}
            {attachments.map((attachment, index) => {
              if (!attachment.blobUri)
                return renderNotSynced(attachment, index);
              else if (attachment.type && imageTypes.includes(attachment.type))
                return renderPhoto(attachment, index);
              else return renderAttachment(attachment, index);
            })}
            {uploadingAttachment &&
              renderAttachmentUploading(uploadingAttachment)}
          </View>
          {props.disabled && attachments.length === 0 && (
            <Text style={ControlsStyleSheet.label}>
              There are no attached files
            </Text>
          )}
        </View>
      )}
      {error ? <Text style={ControlsStyleSheet.error}>{error}</Text> : null}
      <input
        ref={inputRef}
        type='file'
        multiple
        style={{display: 'none'}}
        accept={props.config?.allowedTypes?.join(',')}
        onChange={event => selectFiles(event.target.files)}
      />
      {renderPreviewPhoto()}
      {renderDocumentLibrary()}
      <DismissModal
        isShowingModal={isShowingQuestionModal}
        title='What is this?'
        text="This file was added from a mobile device and has not been synchronized yet. It will be available once the user's device completes synchronization."
        onDismiss={() => setIsShowingQuestionModal(false)}
      />
    </>
  );
};

export default DynamicAttachments;
